import React from "react";

/**
 * Catches render-time errors anywhere below it.
 *
 * Without a boundary, React unmounts the whole tree when any component throws
 * and the user is left looking at a blank white page with nothing in the UI to
 * explain it. That happens more easily here than it should: the API helper
 * returns a plain string in `data` when a request fails, so a page that reads
 * `response.data.questions` gets `undefined` and then throws on `.map()`. A
 * single unreachable request should not blank the site.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Keep the detail in the console for developers; the UI stays plain.
    console.error("Unhandled UI error:", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";

    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px 20px",
          fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
        }}
      >
        <div style={{ maxWidth: 560, textAlign: "center" }}>
          <h1 style={{ fontSize: 22, margin: "0 0 10px", color: "#1f2933" }}>
            Something went wrong on this page
          </h1>
          <p style={{ margin: "0 0 20px", color: "#52606d", lineHeight: 1.6 }}>
            The page could not be displayed. This is usually a temporary problem
            reaching the server.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "10px 22px",
              fontSize: 15,
              color: "#fff",
              background: "#1e3a5c",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Reload the page
          </button>

          {isDev && (
            <pre
              style={{
                marginTop: 24,
                padding: 14,
                textAlign: "left",
                background: "#f4f5f7",
                border: "1px solid #d8dde4",
                borderRadius: 4,
                fontSize: 12.5,
                lineHeight: 1.5,
                color: "#7a2033",
                overflowX: "auto",
                whiteSpace: "pre-wrap",
              }}
            >
              {String(error && (error.stack || error.message || error))}
            </pre>
          )}
        </div>
      </div>
    );
  }
}
